/**
 * Admin panel: paste any LinkedIn URL, optionally choose a target user +
 * career stage / objective, and kick off the full audit + rewrite
 * pipeline. Billing / quota checks are bypassed on the backend.
 *
 * Backend contract: POST /api/admin/analyze-by-url
 *   { linkedin_url, target_user_id?, career_stage?, objective?, ...targeting }
 *   -> { id, status, profile_name, user_id, slug }
 * On success we navigate the admin straight into /analysis/{id} so they
 * can watch progress in the usual audit UI.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Wand2, Loader2, ArrowRight, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

// Career-stage → objective map mirrors backend `full_audit_v2.OBJECTIVES_BY_STAGE`.
// Corporate is the safest default — matches the most common admin use case
// (analyst benchmarking a mid-career professional) AND keeps the audit
// on the v2 pipeline so scores match the user-facing wizard. The legacy
// "__skip__" option was removed because it silently downgraded admin
// runs to the v1 scoring path, producing different scores from a user
// running the same profile through the wizard.
const STAGE_OPTIONS = [
  { key: 'corporate', label: 'Corporate professional' },
  { key: 'student', label: 'Student' },
  { key: 'fresher', label: 'Recent grad / early career' },
];

const OBJECTIVES_BY_STAGE = {
  corporate: [
    'recruiter_visibility_career_growth', 'thought_leadership',
    'business_development', 'investor_visibility',
  ],
  student: ['jobs_internships', 'learning_thought_leadership', 'projects_networking'],
  fresher: ['recruiter_visibility_first_job', 'learning_thought_leadership', 'projects_networking'],
};

const OBJECTIVE_LABELS = {
  recruiter_visibility_career_growth: 'Recruiter visibility & career growth',
  recruiter_visibility_first_job: 'Recruiter visibility & first job',
  thought_leadership: 'Thought leadership',
  business_development: 'Business development',
  investor_visibility: 'Investor visibility (founders)',
  jobs_internships: 'Jobs & internships',
  learning_thought_leadership: 'Learning-led thought leadership',
  projects_networking: 'Projects & networking',
};

export function AdminAnalyzeByUrlPanel() {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [targetUserId, setTargetUserId] = useState('');
  const [stage, setStage] = useState('corporate');
  const [objective, setObjective] = useState('');
  const [targetIndustry, setTargetIndustry] = useState('');
  const [targetGeography, setTargetGeography] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Reset objective whenever stage changes so the dropdown never carries a
  // stale value (e.g., a student objective on a corporate stage).
  useEffect(() => { setObjective(''); }, [stage]);

  const objectiveList = useMemo(() => {
    return OBJECTIVES_BY_STAGE[stage] || [];
  }, [stage]);

  const canSubmit = useMemo(() => {
    if (!url.trim()) return false;
    if (!objective) return false;
    return true;
  }, [url, objective]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setLastResult(null);
    const t = toast.loading('Scraping LinkedIn and starting audit…');
    try {
      const payload = { linkedin_url: url.trim() };
      if (targetUserId.trim()) payload.target_user_id = targetUserId.trim();
      if (stage) {
        payload.career_stage = stage;
        payload.objective = objective;
        if (targetIndustry.trim()) payload.target_industry = targetIndustry.trim();
        if (targetGeography.trim()) payload.target_geography = targetGeography.trim();
      }
      const r = await adminApi.analyzeByUrl(payload);
      setLastResult(r);
      toast.success(`Audit queued for ${r.profile_name || r.slug}. Redirecting…`, { id: t });
      // Small delay so the toast is visible before route change.
      setTimeout(() => navigate(`/analysis/${r.id}`), 700);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not start audit'), { id: t });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-border" data-testid="admin-analyze-by-url-panel">
      <CardContent className="p-0">
        <div className="p-4 border-b border-border flex items-start gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Wand2 size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-semibold">Audit any LinkedIn URL</h2>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Paste a public LinkedIn profile URL and we&rsquo;ll run the full audit + rewrite pipeline.
              Billing / quota checks are bypassed. Attributes to your account by default &mdash; set a target
              user id to attribute the audit inside another user&rsquo;s account.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="admin-analyze-url">LinkedIn URL *</Label>
            <Input
              id="admin-analyze-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.linkedin.com/in/<handle>"
              autoComplete="off"
              spellCheck={false}
              data-testid="admin-analyze-by-url-input"
            />
            <p className="text-[11px] text-muted-foreground">
              Accepts <code className="font-mono text-[10px]">linkedin.com/in/&lt;slug&gt;</code>,
              country subdomains (<code className="font-mono text-[10px]">in.linkedin.com/&hellip;</code>)
              and legacy <code className="font-mono text-[10px]">/pub/</code> URLs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="admin-analyze-user">Target user id (optional)</Label>
              <Input
                id="admin-analyze-user"
                value={targetUserId}
                onChange={(e) => setTargetUserId(e.target.value)}
                placeholder="uuid — leave blank to attribute to yourself"
                data-testid="admin-analyze-by-url-user"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Career stage (optional &mdash; enables Full-Audit v2)</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger data-testid="admin-analyze-by-url-stage">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGE_OPTIONS.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {stage && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Objective *</Label>
                <Select value={objective} onValueChange={setObjective}>
                  <SelectTrigger data-testid="admin-analyze-by-url-objective">
                    <SelectValue placeholder="Pick an objective" />
                  </SelectTrigger>
                  <SelectContent>
                    {objectiveList.map((k) => (
                      <SelectItem key={k} value={k}>{OBJECTIVE_LABELS[k] || k}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="admin-analyze-industry">Target industry</Label>
                <Input
                  id="admin-analyze-industry"
                  value={targetIndustry}
                  onChange={(e) => setTargetIndustry(e.target.value)}
                  placeholder="B2B SaaS, healthtech, …"
                  data-testid="admin-analyze-by-url-industry"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="admin-analyze-geo">Target geography</Label>
                <Input
                  id="admin-analyze-geo"
                  value={targetGeography}
                  onChange={(e) => setTargetGeography(e.target.value)}
                  placeholder="India, US, Singapore, …"
                  data-testid="admin-analyze-by-url-geo"
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 pt-1">
            <div className="flex items-start gap-2 text-[11px] text-muted-foreground max-w-[520px]">
              <Info size={11} className="mt-0.5 shrink-0" />
              <span>
                Costs one Apify scrape + one full LLM audit run. Takes 60&ndash;180 seconds. You&rsquo;ll be redirected to the
                live audit page once queued.
              </span>
            </div>
            <Button
              type="submit"
              disabled={!canSubmit || submitting}
              className="rounded-xl min-w-[160px]"
              data-testid="admin-analyze-by-url-submit"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin mr-2" /> Queuing…
                </>
              ) : (
                <>Start audit <ArrowRight size={14} className="ml-1.5" /></>
              )}
            </Button>
          </div>

          {lastResult && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs" data-testid="admin-analyze-by-url-result">
              <p className="font-medium">Audit queued</p>
              <p className="text-muted-foreground mt-0.5">
                Analysis id: <span className="font-mono">{lastResult.id}</span> &middot; Owner:{' '}
                <span className="font-mono">{lastResult.user_id}</span>
              </p>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

export default AdminAnalyzeByUrlPanel;
